// Sugalarm — CSV export download helpers.
//
// Goes through `authedFetch` so it gets the same bearer + 401 refresh
// dance as `request()`, then reads the body as a Blob and triggers a
// browser download via a hidden <a download>. The endpoints stream the
// CSV from the server (no buffering on the backend) so very large
// pulls are still fine.

import { authedFetch } from "../lib/apiClient";

export type ExportKind = "glucose" | "full";

export interface DownloadResult {
  ok: boolean;
  status: number;
  message?: string;
}

/** Cycle 151: exported so the admin console can drive the same
 *  authed-fetch → blob → <a download> path against its own endpoints. */
export async function downloadCsv(
  path: string,
  suggestedFilename: string,
): Promise<DownloadResult> {
  let res: Response;
  try {
    res = await authedFetch(path);
  } catch (e) {
    return {
      ok: false,
      status: 0,
      message: e instanceof Error ? e.message : "network error",
    };
  }
  if (!res.ok) {
    let detail: string | undefined;
    try {
      const json = await res.json();
      if (json && typeof json === "object" && "detail" in json) {
        detail = String((json as { detail: unknown }).detail);
      }
    } catch {
      /* not JSON */
    }
    return { ok: false, status: res.status, message: detail };
  }

  // Prefer the server's Content-Disposition filename (it embeds the
  // user id-prefix and date range). Fall back to the suggested name.
  const disp = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(disp);
  const filename = m && m[1] ? m[1] : suggestedFilename;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick — Safari occasionally races the click otherwise.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);

  return { ok: true, status: res.status };
}

export function downloadGlucoseCsv(hours = 720): Promise<DownloadResult> {
  return downloadCsv(
    `/api/export/glucose.csv?hours=${hours}`,
    `sugalarm_glucose_${hours}h.csv`,
  );
}

export function downloadFullCsv(hours = 720): Promise<DownloadResult> {
  return downloadCsv(
    `/api/export/full.csv?hours=${hours}`,
    `sugalarm_full_${hours}h.csv`,
  );
}
